import React, { useEffect, useState } from "react";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  LineElement,
  Title,
  Tooltip,
  Legend,
  PointElement,
} from "chart.js";
import { Line } from "react-chartjs-2";
import styled from "styled-components";
import { Tooltip as InfoTooltip } from "react-tooltip";
import { Info } from "lucide-react";

ChartJS.register(CategoryScale, LinearScale, LineElement, Title, Tooltip, Legend, PointElement);

const ChartWrapper = styled.div`
  position: relative;
  background-color: #f9f9f9;
  width: 100%;
  height: 40vh;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  padding: 20px;
`;

const FiltersWrapper = styled.div`
  display: flex;
  gap: 20px;
  margin-bottom: 20px;
  justify-content: center;
`;

const InfoIconWrapper = styled.div`
  position: absolute;
  top: 10px;
  right: 15px;
  cursor: pointer;
  color: #666;

  &:hover {
    color: #000;
  }
`;

const cleanPLNCurrency = (value) => {
  if (!value) return 0;
  return parseFloat(value.toString().replace(/\s/g, "").replace(",", ".").replace(/[^\d.-]/g, "")) || 0;
};

const MonthlyPurchaseComparison = ({ data }) => {
  const [salesData, setSalesData] = useState({});
  const [availableYears, setAvailableYears] = useState([]);
  const [selectedYear, setSelectedYear] = useState(null);
  const [chartData, setChartData] = useState(null);

  const monthLabels = [
    "Styczeń", "Luty", "Marzec", "Kwiecień", "Maj", "Czerwiec",
    "Lipiec", "Sierpień", "Wrzesień", "Październik", "Listopad", "Grudzień"
  ];

  useEffect(() => {
    if (!data || data.length === 0) return;

    const grouped = {};

    data.forEach((row) => {
      let year, month;

      if (row.Rok && row.Miesiac) {
        year = parseInt(row.Rok);
        month = parseInt(row.Miesiac);
      } else if (row.DataZakupu) {
        const date = new Date(row.DataZakupu);
        if (!isNaN(date)) {
          year = date.getFullYear();
          month = date.getMonth() + 1;
        }
      }

      if (!year || !month) return;

      const amount = cleanPLNCurrency(row.K_42); // Use K_42 as base amount

      if (!grouped[year]) grouped[year] = {};
      if (!grouped[year][month]) grouped[year][month] = 0;
      grouped[year][month] += amount;
    });

    const years = Object.keys(grouped).map((y) => parseInt(y));
    const latest = Math.max(...years);

    setSalesData(grouped);
    setAvailableYears(years.sort((a, b) => b - a));
    setSelectedYear(latest);
  }, [data]);

  useEffect(() => {
    if (!selectedYear || !salesData) return;

    const currentYearData = Array.from({ length: 12 }, (_, i) =>
      salesData[selectedYear]?.[i + 1] || 0
    );
    const previousYearData = Array.from({ length: 12 }, (_, i) =>
      salesData[selectedYear - 1]?.[i + 1] || 0
    );

    setChartData({
      labels: monthLabels,
      datasets: [
        {
          label: `Zakupy ${selectedYear} (PLN)`,
          data: currentYearData,
          borderColor: "#9848c7",
          backgroundColor: "rgba(75, 192, 192, 0.2)",
          fill: true,
          tension: 0.4,
        },
        {
          label: `Zakupy ${selectedYear - 1} (PLN)`,
          data: previousYearData,
          borderColor: "rgba(255, 99, 132, 1)",
          backgroundColor: "rgba(255, 99, 132, 0.2)",
          fill: true,
          tension: 0.4,
        },
      ],
    });
  }, [selectedYear, salesData]);

  if (!chartData) return <p>Ładowanie danych zakupów...</p>;

  return (
    <ChartWrapper>
      <InfoIconWrapper data-tooltip-id="tooltip-zakupy-miesiac">
        <Info size={18} />
      </InfoIconWrapper>

      <InfoTooltip
        id="tooltip-zakupy-miesiac"
        place="left"
        content={
          <>
            <strong>Porównanie miesięczne zakupów</strong>
            <br />
            Wartości zakupów w poszczególnych miesiącach<br />
            bieżącego i poprzedniego roku.
          </>
        }
      />

      <FiltersWrapper>
        <div>
          <label htmlFor="year-select">Rok:</label>
          <select
            id="year-select"
            value={selectedYear || ""}
            onChange={(e) => setSelectedYear(parseInt(e.target.value))}
          >
            {availableYears.map((year) => (
              <option key={year} value={year}>{year}</option>
            ))}
          </select>
        </div>
      </FiltersWrapper>

      <Line
        data={chartData}
        options={{
          responsive: true,
          maintainAspectRatio: false,
          scales: {
            y: {
              beginAtZero: true,
              ticks: {
                callback: (value) =>
                  Number(value).toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, " "),
              },
              title: {
                display: true,
                text: "Zakupy (PLN)",
              },
            },
          },
          plugins: {
            title: {
              display: true,
              text: `Zakupy miesięczne: ${selectedYear} vs ${selectedYear - 1}`,
            },
            tooltip: {
              callbacks: {
                label: (context) => {
                  const label = context.dataset.label || "";
                  const value = context.parsed.y || 0;
                  return `${label}: ${value.toLocaleString("pl-PL", {
                    minimumFractionDigits: 2,
                  })}`;
                },
              },
            },
          },
        }}
      />
    </ChartWrapper>
  );
};

export default MonthlyPurchaseComparison;
